/**
 * PRP-MCP-9 work item 8 (G7, F8): the admin user page, over HTTP
 * (`/api/v1/admin/users/{id}/access`, admin only on the server).
 *
 * Its own module, like `adminReviewApi.ts`: `api.ts` sits at its god-file
 * ceiling. The shared `api` instance attaches the Firebase token and unwraps
 * `response.data`. Revoke is the existing grants route (PRP-MCP-7).
 */
import { api } from './api';
import type { ImportAnswer } from './adminReviewApi';

export interface AccessAccount {
  email: string | null;
  name: string | null;
  created_at: string | null;
  is_active: boolean;
  last_login: string | null;
}

export interface AccessSubscription {
  plan: string | null;
  plan_name: string | null;
  status: string | null;
  active: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  founder: boolean;
  founder_until: string | null;
  founder_active: boolean;
  founder_claimed_at: string | null;
  founder_plan: string | null;
}

export interface AccessTrial {
  status: string | null;
  trial_start: string | null;
  trial_end: string | null;
  started_at: string | null;
  converted_at: string | null;
  canceled_at: string | null;
  refused_at: string | null;
  refused_reason: string | null;
  refund_id: string | null;
  refund_error: string | null;
  refund_state: 'refunded' | 'refund_failed' | 'pending' | null;
}

export interface AccessGrant {
  feature: string;
  source: string | null;
  granted_by: string | null;
  expires_at: string | null;
  created_at: string | null;
  active: boolean;
}

export interface LedgerRow {
  created_at: string | null;
  delta: number | null;
  reason: string | null;
  ref: string | null;
  balance_after: number | null;
  included_delta: number | null;
  included_after: number | null;
  source: string | null;
}

export interface AccessCredits {
  included: number;
  included_per_month: number;
  pack: number;
  total: number;
  ledger: LedgerRow[];
}

export interface AccessConsent {
  accepted: boolean;
  version: number | null;
  accepted_at: string | null;
  current_version: number;
  is_current: boolean;
}

export interface ChatEstimate {
  estimate_id: string;
  name: string;
  created_at: string | null;
  from_chat: 'created' | 'changed';
  last_export_at: string | null;
  import_answer: ImportAnswer | null;
  import_answered_at: string | null;
  link: string;
}

export interface GrantPreset {
  id: string;
  label: string;
  days: number | null;
}

/** Comparisons run and the cap in force; `override` null = the default cap (PRP-MCP-10). */
export interface AccessComparisons {
  used: number;
  cap: number;
  override: number | null;
  default_cap: number;
  unlimited: boolean;
}

/** The whole user page. A section whose read failed is null and named in `unavailable`. */
export interface UserAccess {
  user_id: string;
  account: AccessAccount;
  subscription: AccessSubscription | null;
  trial: AccessTrial | null;
  grants: AccessGrant[] | null;
  credits: AccessCredits | null;
  consent: AccessConsent | null;
  chat_estimates: ChatEstimate[] | null;
  snapshots: { count: number; review_link: string } | null;
  comparisons?: AccessComparisons | null;
  /** PRP-MCP-10: the affiliate that referred this user, or null. Absent on an older server. */
  referral?: { affiliate_id: string; code: string | null; name: string | null; referred_at: string | null; link: string } | null;
  unavailable: string[];
  valid_features: string[];
  grant_presets: GrantPreset[];
}

/** The user list's access columns, per user id. */
export interface UserAccessSummary {
  plan: string | null;
  plan_name: string | null;
  plan_status: string | null;
  founder: boolean;
  founder_until: string | null;
  grants_count: number;
  last_chat_at: string | null;
}

/** A preset id from `grant_presets`, or `custom` with `end_date` (YYYY-MM-DD). */
export interface AddGrantBody {
  feature: string;
  preset: string;
  end_date?: string;
}

export const AdminUserAccessAPI = {
  get: (userId: string): Promise<UserAccess> =>
    api.get(`/admin/users/${encodeURIComponent(userId)}/access`),

  addGrant: (
    userId: string,
    body: AddGrantBody,
  ): Promise<{ user_id: string; feature: string; expires_at: string | null; grants: AccessGrant[] }> =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/access/grants`, body),

  revokeGrant: (userId: string, feature: string): Promise<{ revoked: boolean }> =>
    api.delete(`/admin/users/${encodeURIComponent(userId)}/entitlements/${encodeURIComponent(feature)}`),

  /** `cap` null clears the override. */
  setComparisonCap: (userId: string, cap: number | null): Promise<AccessComparisons> =>
    api.post(`/admin/users/${encodeURIComponent(userId)}/access/comparison-cap`, { cap }),

  summary: (): Promise<{ users: Record<string, UserAccessSummary> }> => api.get('/admin/access/users'),
};
